import { Box, Skeleton, Typography } from '@compass/core';
import { PieChart, Export, Series, Label, Connector, Legend, Tooltip, Title, Font } from 'devextreme-react/pie-chart';
import { useParticipantsData } from '../contexts/ParticipantsDataContext';
import { DashboardCard, DashboardCardContext } from '../components/DashboardCard';
import { ParticipantsFilterControls } from './ParticipantsFilterControls';
import { ParticipantStatusChartValue } from '../types/ParticipantStatusData';
import { useRef, useMemo, useContext } from 'react';

interface ChartLabelArg {
    argumentText: string;
    valueText: string;
    value: number;
    percent: number;
    point?: {
        data?: ParticipantStatusChartValue;
    };
}

const ParticipantsChartContent = ({ chartRef }: { chartRef: React.RefObject<PieChart> }) => {
    const { chartData, loading, error, data, filter, programs } = useParticipantsData();
    const { isExpanded } = useContext(DashboardCardContext);

    const styleConfig = isExpanded ? {
        centerTitleSize: 42,
        centerSubSize: 18,
        innerRadius: 0.68,
        labelSize: 14
    } : {
        centerTitleSize: 32,
        centerSubSize: 14,
        innerRadius: 0.65,
        labelSize: 12
    };

    // Prepare metadata for export
    const exportMetadata = useMemo(() => {
        const yearLabel = filter.schoolYear ? `${filter.schoolYear}-${filter.schoolYear + 1}` : 'All Years';
        const programLabel = filter.programId 
            ? programs.find(p => p.id === filter.programId)?.name || 'Unknown Program'
            : 'All Programs';
        
        return {
            title: 'Participants Assisted',
            subtitle: `Year: ${yearLabel} | Program: ${programLabel}`
        };
    }, [filter.schoolYear, filter.programId, programs]);

    const totalParticipants = data?.totalParticipants || 0;

    if (loading) {
        return <Skeleton variant="rounded" height={400} />;
    }

    if (error) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    if (!chartData.length) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                <Typography variant="body2" color="text.secondary">
                    No participants found for selected filters
                </Typography>
            </Box>
        );
    }

    const formatPercentage = (percent: number): string => {
        const pct = percent;
        if (pct > 0 && pct < 1) return '<1';
        return Math.round(pct).toString();
    };

    const centerTemplate = () => {
        return (
            <svg>
                <text 
                    textAnchor="middle" 
                    x="50%" 
                    y="45%" 
                    style={{ 
                        fontSize: `${styleConfig.centerTitleSize}px`, 
                        fontWeight: 'bold',
                        fill: '#000'
                    }}
                >
                    {totalParticipants.toLocaleString()}
                </text>
                <text 
                    textAnchor="middle" 
                    x="50%" 
                    y="55%" 
                    style={{ 
                        fontSize: `${styleConfig.centerSubSize}px`,
                        fill: '#666'
                    }}
                >
                    Total Participants
                </text>
            </svg>
        );
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <ParticipantsFilterControls />
            
            <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                <PieChart
                    ref={chartRef}
                    id="participants-pie-chart"
                    dataSource={chartData}
                    type="doughnut"
                    innerRadius={styleConfig.innerRadius}
                    palette={['#0D47A1', '#00897B', '#FFA726', '#AB47BC']}
                    centerRender={centerTemplate}
                    width="100%"
                    height="100%"
                    resolveLabelOverlapping="shift"
                >
                    <Title text={exportMetadata.title} subtitle={exportMetadata.subtitle}>
                        <Font size={18} weight={600} />
                    </Title>
                    <Series argumentField="label" valueField="value">
                        <Label 
                            visible={true}
                            position="outside"
                            customizeText={(arg: ChartLabelArg) => {
                                const count = arg.point?.data?.count ?? 0;
                                const displayPct = formatPercentage(arg.value);
                                return `${arg.argumentText}\n${count} (${displayPct}%)`;
                            }}
                        >
                            <Font size={styleConfig.labelSize} />
                            <Connector visible={true} width={1} />
                        </Label>
                    </Series>
                    <Legend visible={false} />
                    <Tooltip
                        enabled={true}
                        customizeTooltip={(arg: ChartLabelArg) => {
                            const count = arg.point?.data?.count ?? 0;
                            const displayPct = formatPercentage(arg.value);
                            return {
                                text: `${arg.argumentText}: ${count} (${displayPct}%)`
                            };
                        }}
                    />
                    <Export enabled={true} />
                </PieChart>
            </Box>
        </Box>
    );
};

export const ParticipantsChartCard = () => {
    const { filter, programs } = useParticipantsData();
    const chartRef = useRef<PieChart>(null);

    const handleExport = () => {
        if (chartRef.current) {
            const yearLabel = filter.schoolYear || 'AllYears';
            const programName = filter.programId 
                ? programs.find(p => p.id === filter.programId)?.name?.replace(/\s+/g, '_') || 'Program'
                : 'AllPrograms';
            chartRef.current.instance.exportTo(`Participants_${yearLabel}_${programName}`, 'png');
        }
    };

    return (
        <DashboardCard title="Participants" onExport={handleExport}>
            <ParticipantsChartContent chartRef={chartRef} />
        </DashboardCard>
    );
};